import React from 'react';
import styled from 'styled-components';
import { 
  MessageSquare, 
  FileText, 
  Users, 
  Building2, 
  Home, 
  CreditCard, 
  BarChart3,
  Search,
  Filter,
  Inbox,
  AlertTriangle,
  CheckCircle,
  Clock,
  Send,
  Bell,
  Settings,
  Lock,
  Database,
  WifiOff,
  Plus,
  RotateCcw,
  RefreshCw
} from 'lucide-react';
import { Button } from './Button';

// Empty state types for different use cases
export type EmptyStateType = 
  | 'messages'
  | 'templates'
  | 'users'
  | 'properties'
  | 'units'
  | 'payments'
  | 'announcements'
  | 'reminders'
  | 'notifications'
  | 'search'
  | 'filters'
  | 'analytics'
  | 'reports'
  | 'documents'
  | 'media'
  | 'settings'
  | 'permissions'
  | 'errors'
  | 'offline'
  | 'no-data'
  | 'custom';

// Icon mapping for different empty state types
const EMPTY_STATE_ICONS: Record<EmptyStateType, React.ComponentType<any>> = {
  messages: MessageSquare,
  templates: FileText,
  users: Users,
  properties: Building2,
  units: Home,
  payments: CreditCard,
  announcements: Bell,
  reminders: Clock,
  notifications: Bell,
  search: Search,
  filters: Filter,
  analytics: BarChart3,
  reports: BarChart3,
  documents: FileText,
  media: Inbox,
  settings: Settings,
  permissions: Lock,
  errors: AlertTriangle,
  offline: WifiOff,
  'no-data': Database,
  custom: Inbox
};

// Size variants
export type EmptyStateSize = 'sm' | 'md' | 'lg';

// Styled Components
const EmptyStateContainer = styled.div<{ $size: EmptyStateSize }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${props => {
    switch (props.$size) {
      case 'sm': return '24px';
      case 'md': return '48px';
      case 'lg': return '64px';
      default: return '48px';
    }
  }};
  max-width: 400px;
  margin: 0 auto;
`;

const IconContainer = styled.div<{ $size: EmptyStateSize }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => {
    switch (props.$size) {
      case 'sm': return '16px';
      case 'md': return '24px';
      case 'lg': return '32px';
      default: return '24px';
    }
  }};
  
  svg {
    width: ${props => {
      switch (props.$size) {
        case 'sm': return '48px';
        case 'md': return '64px';
        case 'lg': return '80px';
        default: return '64px';
      }
    }};
    height: ${props => {
      switch (props.$size) {
        case 'sm': return '48px';
        case 'md': return '64px';
        case 'lg': return '80px';
        default: return '64px';
      }
    }};
    color: ${props => props.theme.mutedForeground};
  }
`;

const Title = styled.h3<{ $size: EmptyStateSize }>`
  font-size: ${props => {
    switch (props.$size) {
      case 'sm': return '16px';
      case 'md': return '18px';
      case 'lg': return '20px';
      default: return '18px';
    }
  }};
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin: 0 0 8px 0;
  line-height: 1.4;
`;

const Description = styled.p<{ $size: EmptyStateSize }>`
  font-size: ${props => {
    switch (props.$size) {
      case 'sm': return '14px';
      case 'md': return '14px';
      case 'lg': return '16px';
      default: return '14px';
    }
  }};
  color: ${props => props.theme.mutedForeground};
  margin: 0 0 20px 0;
  line-height: 1.5;
  max-width: 320px;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  
  @media (min-width: 480px) {
    flex-direction: row;
    gap: 8px;
  }
`;

// Main EmptyState component
interface EmptyStateProps {
  type: EmptyStateType;
  title: string;
  description: string;
  size?: EmptyStateSize;
  icon?: React.ComponentType<any>;
  actions?: React.ReactNode;
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  type,
  title,
  description,
  size = 'md',
  icon,
  actions,
  className
}) => {
  const DefaultIcon = icon || EMPTY_STATE_ICONS[type];

  return (
    <EmptyStateContainer $size={size} className={className}>
      <IconContainer $size={size}>
        <DefaultIcon />
      </IconContainer>
      
      <Title $size={size}>{title}</Title>
      <Description $size={size}>{description}</Description>
      
      {actions && (
        <ActionsContainer>
          {actions}
        </ActionsContainer>
      )}
    </EmptyStateContainer>
  );
};

// Predefined empty state configurations
const EMPTY_STATE_CONFIGS: Record<EmptyStateType, { title: string; description: string }> = {
  messages: {
    title: 'No messages found',
    description: 'There are no messages to display. Create your first message to get started.'
  },
  templates: {
    title: 'No templates found',
    description: 'Create your first message template to streamline your communications.'
  },
  users: {
    title: 'No users found',
    description: 'There are no users to display. Add users to get started.'
  },
  properties: {
    title: 'No properties found',
    description: 'There are no properties to display. Add your first property to get started.'
  },
  units: {
    title: 'No units found',
    description: 'There are no units to display. Add units to your properties to get started.'
  },
  payments: {
    title: 'No payments found',
    description: 'There are no payment records to display. Payment history will appear here.'
  },
  announcements: {
    title: 'No announcements found',
    description: 'There are no announcements to display. Create your first announcement to get started.'
  },
  reminders: {
    title: 'No reminders found',
    description: 'There are no reminders to display. Set up reminders to keep tenants informed.'
  },
  notifications: {
    title: 'No notifications found',
    description: 'There are no notifications to display. Notifications will appear here when available.'
  },
  search: {
    title: 'No search results',
    description: 'No items match your search criteria. Try adjusting your search terms or filters.'
  },
  filters: {
    title: 'No filtered results',
    description: 'No items match your current filters. Try adjusting your filter criteria.'
  },
  analytics: {
    title: 'No analytics data',
    description: 'Analytics data will appear here once you have activity to track.'
  },
  reports: {
    title: 'No reports available',
    description: 'Reports will be generated here based on your data and activity.'
  },
  documents: {
    title: 'No documents found',
    description: 'There are no documents to display. Upload or create documents to get started.'
  },
  media: {
    title: 'No media files found',
    description: 'There are no media files to display. Upload images, videos, or other media to get started.'
  },
  settings: {
    title: 'No settings available',
    description: 'Settings will be configured here based on your preferences and requirements.'
  },
  permissions: {
    title: 'No permissions set',
    description: 'Permissions will be configured here to control access to different features.'
  },
  errors: {
    title: 'Something went wrong',
    description: 'An error occurred while loading this content. Please try again or contact support.'
  },
  offline: {
    title: 'You\'re offline',
    description: 'Please check your internet connection and try again.'
  },
  'no-data': {
    title: 'No data available',
    description: 'There is no data to display at the moment. Data will appear here when available.'
  },
  custom: {
    title: 'No items found',
    description: 'There are no items to display. Add items to get started.'
  }
};

// Convenience components for common empty states
export const MessagesEmptyState: React.FC<Omit<EmptyStateProps, 'type' | 'title' | 'description'> & {
  title?: string;
  description?: string;
  onCreateMessage?: () => void;
}> = ({ title, description, onCreateMessage, ...props }) => (
  <EmptyState
    type="messages"
    title={title || EMPTY_STATE_CONFIGS.messages.title}
    description={description || EMPTY_STATE_CONFIGS.messages.description}
    actions={onCreateMessage && (
      <Button variant="primary" size="md" onClick={onCreateMessage}>
        <Plus size={16} />
        Create Message
      </Button>
    )}
    {...props}
  />
);

export const TemplatesEmptyState: React.FC<Omit<EmptyStateProps, 'type' | 'title' | 'description'> & {
  title?: string;
  description?: string;
  onCreateTemplate?: () => void;
}> = ({ title, description, onCreateTemplate, ...props }) => (
  <EmptyState
    type="templates"
    title={title || EMPTY_STATE_CONFIGS.templates.title}
    description={description || EMPTY_STATE_CONFIGS.templates.description}
    actions={onCreateTemplate && (
      <Button variant="primary" size="md" onClick={onCreateTemplate}>
        <Plus size={16} />
        Create Template
      </Button>
    )}
    {...props}
  />
);

export const SearchEmptyState: React.FC<Omit<EmptyStateProps, 'type' | 'title' | 'description'> & {
  title?: string;
  description?: string;
  onClearFilters?: () => void;
}> = ({ title, description, onClearFilters, ...props }) => (
  <EmptyState
    type="search"
    title={title || EMPTY_STATE_CONFIGS.search.title}
    description={description || EMPTY_STATE_CONFIGS.search.description}
    actions={onClearFilters && (
      <Button variant="outline" size="md" onClick={onClearFilters}>
        <RotateCcw size={16} />
        Clear Filters
      </Button>
    )}
    {...props}
  />
);

export const NoDataEmptyState: React.FC<Omit<EmptyStateProps, 'type' | 'title' | 'description'> & {
  title?: string;
  description?: string;
  onRefresh?: () => void;
}> = ({ title, description, onRefresh, ...props }) => (
  <EmptyState
    type="no-data"
    title={title || EMPTY_STATE_CONFIGS['no-data'].title}
    description={description || EMPTY_STATE_CONFIGS['no-data'].description}
    actions={onRefresh && (
      <Button variant="outline" size="md" onClick={onRefresh}>
        <RefreshCw size={16} />
        Refresh
      </Button>
    )}
    {...props}
  />
);

export default EmptyState; 